import { Component, StrictMode } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import "@fontsource-variable/geist";
import "./styles.css";
import { App } from "./App";

const root = document.getElementById("root");
if (!root) throw new Error("Workroom root element is missing");
class ErrorBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  state: { error: string | null } = { error: null };
  static getDerivedStateFromError(error: Error): { error: string } {
    return { error: error.message };
  }
  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(error, info);
    void fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: error.message, stack: error.stack?.slice(0, 5000) }),
    }).catch(() => undefined);
  }
  render(): ReactNode {
    return this.state.error ? (
      <div className="full-state">
        <h1>Something interrupted your workspace.</h1>
        <p>Your saved work is safe. Reload to reconnect.</p>
        <button className="button primary" onClick={() => location.reload()}>
          Reload Workroom
        </button>
      </div>
    ) : (
      this.props.children
    );
  }
}
const client = new QueryClient({
  defaultOptions: { queries: { staleTime: 2000, refetchOnWindowFocus: true } },
});
createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
